const ROOT_ORG_VARIANT_KEYS = new Set(['100', 'org', 'organization', 'root-org', 'root_org'])
const TENANT_VARIANT_KEYS = new Set(['102', 'tenant'])
const ORG_SCOPE_VARIANT_KEYS = new Set([...ROOT_ORG_VARIANT_KEYS, ...TENANT_VARIANT_KEYS])
const SERVICES_PRODUCT_VARIANT_KEYS = new Set(['1400', 'services'])

const IN_APP_CHILD_VARIANT_UNIQUE_IDS = new Set([
  '300', '400', '500', '600', '1200', '1300', '1500', '1600', '1700', '1800', '1900',
  '2000', '2100', '2200', '2300', '2400', '2500', '2600', '2700',
])

function normalizeVariantToken(value?: string | number | null): string {
  return String(value ?? '').trim().toLowerCase()
}

export type ServiceVariantIdentityPayload = {
  service_variant_unique_id?: string | number | null
  service_variant?: string | number | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  service_variant_label?: string | null
  type?: string | null
  service_data?: {
    variant_unique?: string | number | null
    service_name?: string | null
  } | null
}

/** Read variant identity from API payload fields only — no catalog row id remapping. */
export function readServiceVariantIdentity(
  payload?: ServiceVariantIdentityPayload | null,
): { uniqueId: string; label: string } {
  const uniqueId = String(
    payload?.service_variant_unique_id
      ?? payload?.service_data?.variant_unique
      ?? payload?.service_variant
      ?? '',
  ).trim()

  const label = String(
    payload?.service_variant_name
      ?? payload?.service_variant_type
      ?? payload?.service_variant_label
      ?? payload?.service_data?.service_name
      ?? payload?.type
      ?? '',
  ).trim()

  return { uniqueId, label }
}

function trimLevelVariantName(levelVariantName?: string | null): string {
  return String(levelVariantName ?? '').trim()
}

export const SERVICE_VARIANT_TYPE = {
  ORG: '100',
  /** Shared product variant for org-tree service instances (formerly separate ORG_BRANCH / 101). */
  SERVICES: '1400',
  TENANT: '102',
} as const

function isDefiniteNumericVariantId(token: string): boolean {
  return token.length > 0 && /^[0-9]+$/.test(token)
}

export function isRootOrganizationServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  const normalizedUniqueId = normalizeVariantToken(variantUniqueId)
  const normalizedLabel = normalizeVariantToken(variantLabel)

  if (ROOT_ORG_VARIANT_KEYS.has(normalizedUniqueId)) {
    return true
  }

  // Numeric unique ids are product variant codes (100 root ORG, 102 tenant, 1400 Services, …).
  // Ignore display labels like "Organization" when the id is clearly non–root-org.
  if (normalizedUniqueId && isDefiniteNumericVariantId(normalizedUniqueId)) {
    return false
  }

  return ROOT_ORG_VARIANT_KEYS.has(normalizedLabel)
}

export function isTenantOrganizationServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  const normalizedUniqueId = normalizeVariantToken(variantUniqueId)
  const normalizedLabel = normalizeVariantToken(variantLabel)

  if (TENANT_VARIANT_KEYS.has(normalizedUniqueId)) {
    return true
  }

  if (normalizedUniqueId && isDefiniteNumericVariantId(normalizedUniqueId)) {
    return false
  }

  return TENANT_VARIANT_KEYS.has(normalizedLabel)
}

export function isOrganizationServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  const normalizedUniqueId = normalizeVariantToken(variantUniqueId)
  const normalizedLabel = normalizeVariantToken(variantLabel)

  if (ORG_SCOPE_VARIANT_KEYS.has(normalizedUniqueId)) {
    return true
  }

  if (normalizedUniqueId && isDefiniteNumericVariantId(normalizedUniqueId)) {
    return false
  }

  return ORG_SCOPE_VARIANT_KEYS.has(normalizedLabel)
}

export function isServicesProductVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  const normalizedUniqueId = normalizeVariantToken(variantUniqueId)
  const normalizedLabel = normalizeVariantToken(variantLabel)

  if (SERVICES_PRODUCT_VARIANT_KEYS.has(normalizedUniqueId)) {
    return true
  }

  if (SERVICES_PRODUCT_VARIANT_KEYS.has(normalizedLabel)) {
    return true
  }

  if (normalizedUniqueId && isDefiniteNumericVariantId(normalizedUniqueId)) {
    return false
  }

  return false
}

/** Root org, tenant, or branch Services (1400) — not in-app child instances such as membership (400). */
export function isPrimaryWorkspaceServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  return isOrganizationServiceVariant(variantUniqueId, variantLabel)
    || isServicesProductVariant(variantUniqueId, variantLabel)
}

/** Child service instances provisioned under a branch (membership, fundraising, etc.). */
export function isInAppChildServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  const normalizedUniqueId = normalizeVariantToken(variantUniqueId)
  const normalizedLabel = normalizeVariantToken(variantLabel)

  if (!normalizedUniqueId && !normalizedLabel) {
    return false
  }

  if (isPrimaryWorkspaceServiceVariant(variantUniqueId, variantLabel)) {
    return false
  }

  if (IN_APP_CHILD_VARIANT_UNIQUE_IDS.has(normalizedUniqueId)) {
    return true
  }

  const inAppLabels = ['membership', 'fundraising', 'charity', 'academic', 'acadamic']
  return inAppLabels.some((token) => normalizedLabel === token || normalizedLabel.includes(token))
}

/** @deprecated Prefer isBranchHierarchyTier / isBranchOperatorWorkspaceContextFromPost; kept for call sites that mean "Services line" (1400). */
export function isOrgBranchServiceVariant(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): boolean {
  return isServicesProductVariant(variantUniqueId, variantLabel)
}

export function getCanonicalServiceVariantLabel(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): string {
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return 'ROOT-ORG'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return 'TENANT'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return 'SERVICES'
  }

  return String(variantLabel ?? '').trim()
}

export function getServiceVariantAdminRoleLabel(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
): string {
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return 'Root Admin'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return 'Primary role'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return 'Branch Admin'
  }

  return 'Admin'
}

/** User-facing scope name for navigation and copy (replaces generic “Service instance”). */
export function getServiceInstanceScopeLabel(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Root Org'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Tenant'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Branch'
  }

  return 'Service Instance'
}

/** User-facing page title for organization tree / setup flows. */
export function getOrganizationTreeSetupLabel(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} Setup` : 'Tenant Setup'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} Setup` : 'Branch Setup'
  }

  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} Setup` : 'Root Organization Setup'
  }

  return lv ? `${lv} Setup` : 'Organization Setup'
}

/** Short label for the tree workspace hero badge (variant-specific, avoids “node”). */
export function getTreeWorkspaceHeroBadgeLabel(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Root organization'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Tenant'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return lv || 'Branch'
  }

  return lv || 'Organization'
}

/** Phrase like “this tenant” for card copy that refers to the current structure scope. */
export function getTreeWorkspaceLocationScopePhrase(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `this ${lv}` : 'this root organization'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `this ${lv}` : 'this tenant'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `this ${lv}` : 'this branch'
  }

  return lv ? `this ${lv}` : 'this organization'
}

/** Intro paragraph for the tree workspace main card. */
export function getTreeWorkspaceDescription(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    if (lv) {
      return `This area is part of your ${lv}. From here you can open child branches, work with policies and details, and use the vendor directory flows.`
    }
    return 'This area is part of your root organization structure. From here you can open child branches, work with policies and details, and use the vendor directory flows.'
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    if (lv) {
      return `This area is part of your ${lv}. From here you can open downstream branches, manage policies and details, and use the vendor directory flows.`
    }
    return 'This area is part of your tenant. From here you can open downstream branches, manage policies and details, and use the vendor directory flows.'
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    if (lv) {
      return `This area is part of your ${lv}. From here you can open nested branches, manage policies and details, and use the vendor directory flows.`
    }
    return 'This area is part of your branch. From here you can open nested branches, manage policies and details, and use the vendor directory flows.'
  }

  if (lv) {
    return `This area is part of your ${lv}. From here you can open branches, work with policies and details, and use the vendor directory flows.`
  }

  return 'This area is part of your organization. From here you can open branches, work with policies and details, and use the vendor directory flows.'
}

export function getTreeStructureFallbackHeading(
  id: string | number,
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): string {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} location ${id}` : `Organization location ${id}`
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} location ${id}` : `Tenant location ${id}`
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return lv ? `${lv} location ${id}` : `Branch location ${id}`
  }

  return lv ? `${lv} location ${id}` : `Location ${id}`
}

export type TreeStructureEditCopy = {
  editButton: string
  dialogTitle: string
  dialogDescription: string
  nameFieldLabel: string
  nameRequiredError: string
  updateSuccessMessage: string
}

export function getTreeStructureEditCopy(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
  levelVariantName?: string | null,
): TreeStructureEditCopy {
  const lv = trimLevelVariantName(levelVariantName)
  if (isRootOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return {
      editButton: lv ? `Edit ${lv} title` : 'Edit organization title',
      dialogTitle: lv ? `Edit ${lv} title` : 'Edit organization title',
      dialogDescription: lv
        ? `Update the display name shown for this place in your ${lv} structure.`
        : 'Update the display name shown for this place in your organization structure.',
      nameFieldLabel: 'Display name',
      nameRequiredError: 'Display name is required',
      updateSuccessMessage: 'Title updated successfully',
    }
  }

  if (isTenantOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return {
      editButton: lv ? `Edit ${lv} title` : 'Edit tenant title',
      dialogTitle: lv ? `Edit ${lv} title` : 'Edit tenant title',
      dialogDescription: lv
        ? `Update the display name shown for this place in your ${lv} structure.`
        : 'Update the display name shown for this place in your tenant structure.',
      nameFieldLabel: 'Display name',
      nameRequiredError: 'Display name is required',
      updateSuccessMessage: 'Title updated successfully',
    }
  }

  if (isOrgBranchServiceVariant(variantUniqueId, variantLabel)) {
    return {
      editButton: lv ? `Edit ${lv} title` : 'Edit branch title',
      dialogTitle: lv ? `Edit ${lv} title` : 'Edit branch title',
      dialogDescription: lv
        ? `Update the display name shown for this place in your ${lv} structure.`
        : 'Update the display name shown for this place in your branch structure.',
      nameFieldLabel: 'Display name',
      nameRequiredError: 'Display name is required',
      updateSuccessMessage: 'Title updated successfully',
    }
  }

  return {
    editButton: lv ? `Edit ${lv} title` : 'Edit title',
    dialogTitle: lv ? `Edit ${lv} title` : 'Edit title',
    dialogDescription: lv
      ? `Update the display name shown for this place in your ${lv} structure.`
      : 'Update the display name shown for this place in your structure.',
    nameFieldLabel: 'Display name',
    nameRequiredError: 'Display name is required',
    updateSuccessMessage: 'Title updated successfully',
  }
}
